import { useState } from 'react';
import { useToast } from "@/hooks/use-toast";
import { updateUserProfile, updateCompanyInfo } from './services/profileService';
import { saveAuthData } from './authUtils';
import { User, ProfileUpdateData, CompanyInfo } from './types';

export const useProfileUpdate = (
  user: User | null,
  token: string | null,
  setUser: (user: User | null) => void
) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const updateProfile = async (data: ProfileUpdateData): Promise<boolean> => {
    if (!user || !token) return false;

    setIsLoading(true);

    if (!data.owner) {
      data.owner = 'me';
    }

    console.log('User before profile update:', user);

    const userProfileId = user.userProfile['@id'];
    if (!userProfileId) {
      console.error('Missing user profile ID', user.userProfile);
      toast({
        title: "Profil frissítési hiba",
        description: "A felhasználói profil azonosítója hiányzik.",
        variant: "destructive",
      });
      setIsLoading(false);
      return false;
    }

    console.log('Updating profile with data:', data);

    // Optimistically update the user state
    const optimisticUser = {
      ...user,
      userProfile: {
        ...user.userProfile,
        ...data,
      },
    };
    setUser(optimisticUser);

    const result = await updateUserProfile(data, token, userProfileId);

    if (result.success && result.updatedUserProfile) {
      const updatedUser = {
        ...user,
        userProfile: result.updatedUserProfile,
      };

      console.log('Updated user after profile update:', updatedUser);

      // Save the updated user to local storage
      saveAuthData(token, updatedUser);
      setUser(updatedUser);

      toast({
        title: "Sikeres profil frissítés",
        description: "Profilja sikeresen frissítve.",
      });
    } else {
      // Revert to the previous user state if the update fails
      setUser(user);

      toast({
        title: "Profil frissítési hiba",
        description: result.message || "Hiba történt a profil frissítése során.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
    return result.success;
  };

  const updateCompanyInformation = async (companyInfo: CompanyInfo): Promise<boolean> => {
    if (!user || !token) return false;

    setIsLoading(true);

    const userProfileId = user.userProfile['@id'];
    if (!userProfileId) {
      console.error('Missing user profile ID', user.userProfile);
      toast({
        title: "Céginformáció frissítési hiba",
        description: "A felhasználói profil azonosítója hiányzik.",
        variant: "destructive",
      });
      setIsLoading(false);
      return false;
    }

    console.log('Updating company info:', companyInfo);

    // Use the profile update mechanism to update company info
    const profileData: ProfileUpdateData = {
      owner: 'me',
      name: user.userProfile.name,
      email: user.email,
      companyInfo: companyInfo
    };

    const result = await updateUserProfile(profileData, token, userProfileId);

    if (result.success && result.updatedUserProfile) {
      // Update the user in state with the new company info
      const updatedUser = {
        ...user,
        userProfile: {
          ...user.userProfile,
          companyInfo: result.updatedUserProfile.companyInfo,
        },
      };

      console.log('Updated user after company info update:', updatedUser);

      // Save the updated user to local storage
      saveAuthData(token, updatedUser);
      setUser(updatedUser);

      toast({
        title: "Sikeres frissítés",
        description: "Céges adatok sikeresen frissítve.",
      });
    } else {
      toast({
        title: "Céginformáció frissítési hiba",
        description: result.message || "Hiba történt a céges adatok frissítése során.",
        variant: "destructive",
      });
    }

    setIsLoading(false);
    return result.success;
  };

  return { updateProfile, updateCompanyInformation, isLoading };
};
